import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CreditCard, Banknote, ShieldCheck, ChevronRight, HelpCircle, Camera, Video, MessageSquareText, Gift } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { inr, cx } from '@/lib/format';
import { useStore } from '@/context/StoreContext';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { openRazorpay } from '@/lib/razorpay';
import InquiryDialog from '@/components/common/InquiryDialog';

export default function Checkout() {
  const { cart, cartSubtotal, clearCart, user, isAuthed } = useStore();
  const navigate = useNavigate();
  const [payment, setPayment] = useState('cod');
  const [placing, setPlacing] = useState(false);
  const coupon = localStorage.getItem('gaze_coupon') || null;
  const gift = localStorage.getItem('gaze_gift') || '';
  const [applied, setApplied] = useState(null);
  const [addr, setAddr] = useState({ full_name: user?.name || '', phone: user?.phone || '', line1: '', line2: '', city: '', state: '', pincode: '' });
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState(null);
  const [showAddrForm, setShowAddrForm] = useState(true);
  const [email, setEmail] = useState(user?.email || '');
  const [notes, setNotes] = useState('');
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquiryType, setInquiryType] = useState('question');
  const [gw, setGw] = useState(null);
  const [giftWrap, setGiftWrap] = useState(false);
  const [giftNote, setGiftNote] = useState('');
  const [cfg, setCfg] = useState({
    shipping: { free_shipping_threshold: 1999, base_shipping: 99, min_order_value: 0 },
    payments: { razorpay_enabled: true, cod_enabled: true, cod_fee: 0, cod_max_order: 0 },
  });
  const placedRef = useRef(false);

  useEffect(() => { api.get('/gift-wrap').then((r) => setGw(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get('/checkout/config').then((r) => { if (r.data?.shipping && r.data?.payments) setCfg(r.data); }).catch(() => {}); }, []);
  useEffect(() => {
    if (!isAuthed) return;
    api.get('/users/me/addresses').then((r) => {
      const list = r.data || [];
      setSavedAddresses(list);
      const def = list.find((a) => a.is_default) || list[0];
      if (def) {
        setAddr({ full_name: def.full_name, phone: def.phone, line1: def.line1, line2: def.line2 || '', city: def.city, state: def.state, pincode: def.pincode });
        setSelectedAddrId(def.id);
        setShowAddrForm(false);
      }
    }).catch(() => {});
  }, [isAuthed]);

  const chooseAddress = (a) => {
    setAddr({ full_name: a.full_name, phone: a.phone, line1: a.line1, line2: a.line2 || '', city: a.city, state: a.state, pincode: a.pincode });
    setSelectedAddrId(a.id);
    setShowAddrForm(false);
  };
  const useNewAddress = () => {
    setSelectedAddrId(null);
    setAddr({ full_name: user?.name || '', phone: user?.phone || '', line1: '', line2: '', city: '', state: '', pincode: '' });
    setShowAddrForm(true);
  };

  useEffect(() => { if (!placedRef.current && cart.length === 0) navigate('/cart'); }, [cart.length]);
  useEffect(() => {
    if (coupon && cartSubtotal > 0) api.post('/coupons/validate', { code: coupon, subtotal: cartSubtotal }).then((r) => { if (r.data.valid) setApplied(r.data); }).catch(() => {});
  }, [coupon, cartSubtotal]);

  const discount = applied ? applied.discount : 0;
  const freeAt = Number(cfg.shipping.free_shipping_threshold ?? 1999);
  const baseShip = Number(cfg.shipping.base_shipping ?? 99);
  const minOrder = Number(cfg.shipping.min_order_value ?? 0);
  const shipping = freeAt <= 0 || cartSubtotal - discount >= freeAt ? 0 : baseShip;
  const wrapFee = giftWrap && gw?.enabled ? Number(gw.fee || 0) : 0;
  const codFee = payment === 'cod' ? Number(cfg.payments.cod_fee || 0) : 0;
  const total = Math.max(0, cartSubtotal - discount + shipping + wrapFee + codFee);

  // Payment availability (admin-controlled)
  const codMax = Number(cfg.payments.cod_max_order || 0);
  const totalBeforeCodFee = Math.max(0, cartSubtotal - discount + shipping + wrapFee);
  const codOverLimit = codMax > 0 && totalBeforeCodFee + Number(cfg.payments.cod_fee || 0) > codMax;
  const codUsable = cfg.payments.cod_enabled && !codOverLimit;
  const razorpayUsable = !!cfg.payments.razorpay_enabled;
  const belowMinOrder = minOrder > 0 && cartSubtotal - discount < minOrder;
  const noPaymentAvailable = !codUsable && !razorpayUsable;

  // Auto-switch away from an unavailable method
  useEffect(() => {
    if (payment === 'cod' && !codUsable && razorpayUsable) setPayment('razorpay');
    else if (payment === 'razorpay' && !razorpayUsable && codUsable) setPayment('cod');
  }, [payment, codUsable, razorpayUsable]);

  const validate = () => {
    for (const f of ['full_name', 'phone', 'line1', 'city', 'state', 'pincode']) if (!addr[f]) { toast.error('Please complete your address'); return false; }
    if (addr.phone.length < 10) { toast.error('Enter a valid phone number'); return false; }
    if (addr.pincode.length !== 6) { toast.error('Enter a valid 6-digit pincode'); return false; }
    if (!isAuthed && !email) { toast.error('Please enter your email'); return false; }
    return true;
  };

  const submitOrder = async (extra = {}) => {
    const payload = {
      items: cart.map((i) => ({ product_id: i.id, qty: i.qty, variant: i.variant })),
      address: addr, email, payment_method: payment,
      coupon_code: applied ? applied.code : null, gift_message: gift || null, order_notes: notes || null,
      gift_wrap: !!(giftWrap && gw?.enabled), gift_note: giftWrap && giftNote.trim() ? giftNote.trim() : null,
      ...extra,
    };
    const { data } = await api.post('/orders', payload);
    placedRef.current = true;
    clearCart();
    localStorage.removeItem('gaze_coupon'); localStorage.removeItem('gaze_gift');
    navigate(`/order-success/${data.order_number}`, { state: { order: data } });
  };

  const placeOrder = async () => {
    if (!validate()) return;
    setPlacing(true);
    try {
      if (payment === 'razorpay') {
        const { data: rp } = await api.post('/payments/razorpay/create-order', { amount: total, receipt: `gz-${Date.now()}` });
        if (rp && rp.stub === false) {
          // Live Razorpay: open checkout, then place order with verified payment ids
          const paid = await openRazorpay({
            order: rp,
            prefill: { name: addr.full_name, email, contact: addr.phone },
            onSuccess: async (res) => {
              try {
                await submitOrder({
                  razorpay_order_id: res.razorpay_order_id,
                  razorpay_payment_id: res.razorpay_payment_id,
                  razorpay_signature: res.razorpay_signature,
                });
              } catch (e) { toast.error(e?.response?.data?.detail || 'Payment could not be confirmed'); }
            },
            onDismiss: () => { toast('Payment cancelled'); setPlacing(false); },
          });
          if (!paid) { setPlacing(false); return; }
          return;
        }
        // Stub/demo mode: place order directly (marked paid on backend)
        await submitOrder();
        return;
      }
      await submitOrder();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not place order');
    } finally { setPlacing(false); }
  };

  const field = 'w-full rounded-lg border border-border px-3 py-2.5 text-sm outline-none focus:border-[hsl(var(--gaze-gold))]';

  return (
    <div className="container-gaze py-8">
      <nav className="mb-2 flex items-center gap-1 text-xs text-muted-foreground"><Link to="/cart">Cart</Link><ChevronRight size={12} /><span className="text-foreground">Checkout</span></nav>
      <h1 className="text-3xl font-semibold">Checkout</h1>
      <div className="mt-6 grid gap-8 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {!isAuthed && (
            <div className="rounded-2xl border border-border bg-white p-5">
              <div className="flex items-center justify-between"><h2 className="text-lg font-semibold">Contact</h2><Link to="/login" className="text-xs font-semibold text-[hsl(var(--primary))]">Login for faster checkout</Link></div>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email address" className={cx(field, 'mt-3')} data-testid="checkout-email-input" />
            </div>
          )}
          <div className="rounded-2xl border border-border bg-white p-5" data-testid="checkout-address-form">
            <h2 className="mb-3 text-lg font-semibold">Shipping Address</h2>
            {isAuthed && savedAddresses.length > 0 && (
              <div className="mb-4 space-y-2">
                {savedAddresses.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => chooseAddress(a)}
                    data-testid={`checkout-saved-address-${a.id}`}
                    className={cx(
                      'w-full rounded-xl border p-3 text-left text-sm transition',
                      selectedAddrId === a.id && !showAddrForm ? 'border-[hsl(var(--gaze-gold))] bg-[hsl(var(--gaze-ivory))]' : 'border-border hover:bg-[hsl(var(--gaze-ivory))]/50'
                    )}
                  >
                    <p className="font-semibold">{a.full_name} {a.is_default && <span className="ml-2 rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5 text-[10px] font-normal">Default</span>}</p>
                    <p className="mt-0.5 text-muted-foreground">{a.line1}, {a.city}, {a.state} - {a.pincode}</p>
                    <p className="text-muted-foreground">{a.phone}</p>
                  </button>
                ))}
                <button type="button" onClick={useNewAddress} data-testid="checkout-add-new-address" className={cx('w-full rounded-xl border p-3 text-left text-sm font-semibold transition', showAddrForm ? 'border-[hsl(var(--gaze-gold))] bg-[hsl(var(--gaze-ivory))]' : 'border-dashed border-border hover:bg-[hsl(var(--gaze-ivory))]/50')}>
                  + Use a new address
                </button>
              </div>
            )}
            {showAddrForm && (
            <div className="grid gap-3 sm:grid-cols-2">
              <input value={addr.full_name} onChange={(e) => setAddr({ ...addr, full_name: e.target.value })} placeholder="Full name" className={field} data-testid="checkout-name-input" />
              <input value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value.replace(/\D/g, '') })} placeholder="Phone number" maxLength={10} className={field} data-testid="checkout-phone-input" />
              <input value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} placeholder="Address line 1" className={cx(field, 'sm:col-span-2')} data-testid="checkout-line1-input" />
              <input value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} placeholder="Address line 2 (optional)" className={cx(field, 'sm:col-span-2')} />
              <input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} placeholder="City" className={field} data-testid="checkout-city-input" />
              <input value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} placeholder="State" className={field} data-testid="checkout-state-input" />
              <input value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value.replace(/\D/g, '') })} placeholder="Pincode" maxLength={6} className={field} data-testid="checkout-pincode-input" />
            </div>
            )}
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Order notes (optional)" rows={2} className={cx(field, 'mt-3')} />
          </div>


          <div className="rounded-2xl border border-border bg-white p-5">
            <h2 className="mb-3 text-lg font-semibold">Payment Method</h2>
            {noPaymentAvailable ? (
              <p className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800" data-testid="checkout-no-payment-warning">
                No payment methods are currently available. Please contact us to complete your order.
              </p>
            ) : (
              <RadioGroup value={payment} onValueChange={setPayment} className="space-y-3">
                {cfg.payments.cod_enabled && (
                  <label className={cx('flex items-center gap-3 rounded-xl border p-4', codUsable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60', payment === 'cod' ? 'border-[hsl(var(--primary))] bg-[hsl(var(--gaze-ivory))]' : 'border-border')}>
                    <RadioGroupItem value="cod" disabled={!codUsable} data-testid="checkout-cod-radio" />
                    <Banknote size={20} className="text-[hsl(var(--primary))]" />
                    <div>
                      <p className="text-sm font-semibold">Cash on Delivery{Number(cfg.payments.cod_fee) > 0 && <span className="ml-1.5 text-xs font-bold text-[hsl(var(--gaze-gold))]">+{inr(cfg.payments.cod_fee)} fee</span>}</p>
                      <p className="text-xs text-muted-foreground">
                        {codOverLimit ? `Available only for orders up to ${inr(codMax)}` : 'Pay when your order arrives'}
                      </p>
                    </div>
                  </label>
                )}
                {razorpayUsable && (
                  <label className={cx('flex cursor-pointer items-center gap-3 rounded-xl border p-4', payment === 'razorpay' ? 'border-[hsl(var(--primary))] bg-[hsl(var(--gaze-ivory))]' : 'border-border')}>
                    <RadioGroupItem value="razorpay" data-testid="checkout-razorpay-radio" />
                    <CreditCard size={20} className="text-[hsl(var(--primary))]" />
                    <div><p className="text-sm font-semibold">Online Payment (Razorpay)</p><p className="text-xs text-muted-foreground">UPI, Cards, Netbanking</p></div>
                  </label>
                )}
              </RadioGroup>
            )}
          </div>

          {gw?.enabled && (
            <div className="rounded-2xl border border-border bg-white p-5" data-testid="checkout-gift-wrap-panel">
              <label className="flex cursor-pointer items-start gap-3">
                <input type="checkbox" checked={giftWrap} onChange={(e) => setGiftWrap(e.target.checked)} className="mt-1" data-testid="checkout-gift-wrap-checkbox" />
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[hsl(var(--gaze-ivory))]"><Gift size={18} className="gaze-gold" /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{gw.title || 'Gift wrap this order'} <span className="ml-1 text-xs font-bold text-[hsl(var(--gaze-gold))]">{Number(gw.fee) > 0 ? `+${inr(gw.fee)}` : 'Free'}</span></p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{gw.description}</p>
                </div>
              </label>
              {giftWrap && (
                <textarea value={giftNote} onChange={(e) => setGiftNote(e.target.value)} maxLength={200} rows={2} placeholder="Personal note card message (optional, max 200 chars)" className={cx(field, 'mt-3')} data-testid="checkout-gift-note-input" />
              )}
            </div>
          )}

          {/* Need help before placing order? */}
          <div className="rounded-2xl border border-[hsl(var(--gaze-gold))]/40 bg-[hsl(var(--gaze-ivory))] p-5" data-testid="checkout-inquiry-panel">
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white text-[hsl(var(--primary))]"><MessageSquareText size={18} /></div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">Need help before placing your order?</p>
                <p className="mt-0.5 text-xs text-muted-foreground">Get real photos, a video, or ask any question about your items. We&apos;ll respond quickly on email or phone.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" onClick={() => { setInquiryType('photos'); setInquiryOpen(true); }} data-testid="checkout-ask-photos" className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-[hsl(var(--primary))] shadow-sm ring-1 ring-inset ring-border"><Camera size={12} /> Real photos</button>
                  <button type="button" onClick={() => { setInquiryType('video'); setInquiryOpen(true); }} data-testid="checkout-ask-video" className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-[hsl(var(--primary))] shadow-sm ring-1 ring-inset ring-border"><Video size={12} /> Product video</button>
                  <button type="button" onClick={() => { setInquiryType('question'); setInquiryOpen(true); }} data-testid="checkout-ask-question" className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-[hsl(var(--primary))] shadow-sm ring-1 ring-inset ring-border"><HelpCircle size={12} /> Ask a question</button>
                  <button type="button" onClick={() => { setInquiryType('note'); setInquiryOpen(true); }} data-testid="checkout-ask-note" className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--primary))] px-3 py-1.5 text-xs font-semibold text-white">Add a note</button>
                </div>
              </div>
            </div>
          </div>

          <InquiryDialog open={inquiryOpen} onOpenChange={setInquiryOpen} source="checkout" defaultType={inquiryType} orderNumber={null} />
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-24 rounded-2xl border border-border bg-white p-5">
            <h3 className="mb-3 text-sm font-semibold">Order Summary</h3>
            <div className="max-h-52 space-y-3 overflow-y-auto">
              {cart.map((it) => (
                <div key={it.key} className="flex items-center gap-3">
                  <div className="relative"><img src={it.image} alt={it.name} className="h-12 w-12 rounded-lg object-cover" /><span className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-[hsl(var(--primary))] text-[10px] font-bold text-white">{it.qty}</span></div>
                  <p className="flex-1 truncate text-xs">{it.name}</p>
                  <span className="text-xs font-semibold">{inr(it.price * it.qty)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-2 border-t border-border pt-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{inr(cartSubtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-green-700"><span>Discount ({applied.code})</span><span>-{inr(discount)}</span></div>}
              <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span>{shipping === 0 ? 'Free' : inr(shipping)}</span></div>
              {wrapFee > 0 && <div className="flex justify-between" data-testid="checkout-gift-wrap-line"><span className="text-muted-foreground">Gift wrap</span><span>{inr(wrapFee)}</span></div>}
              {codFee > 0 && <div className="flex justify-between" data-testid="checkout-cod-fee-line"><span className="text-muted-foreground">COD fee</span><span>{inr(codFee)}</span></div>}
              <div className="flex justify-between border-t border-border pt-2 text-base font-bold"><span>Total</span><span>{inr(total)}</span></div>
            </div>
            {belowMinOrder && (
              <p className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800" data-testid="checkout-min-order-warning">
                Minimum order value is {inr(minOrder)}. Add {inr(Math.ceil(minOrder - (cartSubtotal - discount)))} more to place your order.
              </p>
            )}
            <button onClick={placeOrder} disabled={placing || belowMinOrder || noPaymentAvailable} data-testid="checkout-place-order-button" className="mt-4 w-full rounded-full bg-[hsl(var(--primary))] py-3.5 text-sm font-semibold text-white disabled:opacity-60">{placing ? 'Placing order...' : `Place Order · ${inr(total)}`}</button>
            <p className="mt-3 flex items-center justify-center gap-1 text-xs text-muted-foreground"><ShieldCheck size={13} /> Secure checkout</p>
          </div>
        </div>
      </div>
    </div>
  );
}